import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useWebSocket } from '../hooks/useWebSocket'
import { generateNotificationId } from '../state/notifications'
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  Collapse,
  Typography,
  Badge,
  Button,
  Tooltip,
  useTheme,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Task as TaskIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ManageAccounts as ManageAccountsIcon,
  Chat as ChatIcon,
  Email as EmailIcon,
  Inventory as InventoryIcon,
  AdminPanelSettings as AdminIcon,
  ExpandLess,
  ExpandMore,
  AssignmentTurnedIn as StageGatesIcon,
  Psychology as AIPromptsIcon,
  AutoStories as CompanyContextIcon,
  Explore as NorthStarIcon,
  Description as ResumeTemplatesIcon,
  Settings as SettingsIcon,
  Feedback as FeedbackIcon,
  AccountCircle as ProfileIcon,
  TravelExplore as TravelExploreIcon,
  Forum as ForumIcon,
  Contacts as ContactsIcon,
  AccountTree as AccountTreeIcon,
  Checklist as ChecklistIcon,
  MailOutline as MailOutlineIcon,
  BusinessCenter as BusinessCenterIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material'
import { signOut } from 'aws-amplify/auth'
import { userAtom } from '../state/auth'
import { notificationAtom } from '../state/notifications'
import { colors } from '../theme'
import GlobalSearchBar from './GlobalSearchBar'
import BrandLogo from './BrandLogo'
import BackButton from './common/BackButton'
import { useFeedback } from '../context/FeedbackContext'
import FeedbackModal from './feedback/FeedbackModal'
import { isAdminOrManager } from '../utils/roles'

const drawerWidth = 260
const collapsedWidth = 64

interface LayoutProps {
  children: React.ReactNode
}

interface NavItem {
  text: string
  icon: React.ReactNode
  path: string
  roles?: string[]
  section: string
  /** Visible to every staff member, even outside the roles/section gates. */
  staffVisible?: boolean
}

const sectionOrder = ['jobSearch', 'communication', 'crm', 'admin'] as const

const sections: Record<string, { name: string; staffName?: string; icon: React.ReactNode }> = {
  jobSearch: { name: 'Go-To-Market', icon: <TravelExploreIcon /> },
  communication: { name: 'Communication', icon: <ForumIcon /> },
  crm: { name: 'CRM', icon: <ContactsIcon /> },
  // MM-9: plain staff only see Users here, so "Admin" would overstate what the
  // section grants them.
  admin: { name: 'Admin', staffName: 'Team', icon: <AdminIcon /> },
}

// Top-level links, not nested in a section. Every staff member sees these.
const standaloneNavItems: { text: string; icon: React.ReactNode; path: string }[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Client Pipeline', icon: <AccountTreeIcon />, path: '/client-pipeline' },
]

const allNavItems: NavItem[] = [
  // Job Search
  // Networking is its own activity, not a step inside an application — a
  // connection only becomes an application once it turns into an opportunity.
  { text: 'Side Door', icon: <GroupsIcon />, path: '/networking', section: 'jobSearch' },
  { text: 'Front Door', icon: <BusinessCenterIcon />, path: '/opportunities', section: 'jobSearch' },

  // Communication
  { text: 'SMS Inbox', icon: <ChatIcon />, path: '/messages', section: 'communication' },
  { text: 'Email Inbox', icon: <EmailIcon />, path: '/emails', section: 'communication' },

  // CRM
  { text: 'Leads', icon: <PersonAddIcon />, path: '/leads', section: 'crm' },
  { text: 'Clients', icon: <PeopleIcon />, path: '/clients', section: 'crm' },
  { text: 'Tasks', icon: <TaskIcon />, path: '/tasks', section: 'crm' },

  // Admin
  { text: 'Products', icon: <InventoryIcon />, path: '/products', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'Stage Gates', icon: <StageGatesIcon />, path: '/stage-gates', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'AI Prompts', icon: <AIPromptsIcon />, path: '/ai-prompts', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'Company Context', icon: <CompanyContextIcon />, path: '/company-context', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'North Star Templates', icon: <NorthStarIcon />, path: '/north-star-templates', roles: ['admin'], section: 'admin' },
  { text: 'Resume Templates', icon: <ResumeTemplatesIcon />, path: '/resume-templates', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'Outreach Checklists', icon: <ChecklistIcon />, path: '/outreach-checklists', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'Outreach Templates', icon: <MailOutlineIcon />, path: '/outreach-templates', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['admin', 'manager'], section: 'admin' },
  { text: 'Users', icon: <ManageAccountsIcon />, path: '/users', roles: ['admin', 'manager'], section: 'admin', staffVisible: true },
]

export default function Layout({ children }: LayoutProps) {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [user] = useAtom(userAtom)
  const [notification, setNotification] = useAtom(notificationAtom)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const [unreadSmsCount, setUnreadSmsCount] = useState(0)
  const [unreadEmailCount, setUnreadEmailCount] = useState(0)

  const isAdmin = isAdminOrManager(user)

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    sectionOrder.forEach(key => { initial[key] = false })
    const currentItem = allNavItems.find(item => location.pathname === item.path)
    if (currentItem) initial[currentItem.section] = true
    return initial
  })

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))
  }

  // MWA-208: suppress per-section <Collapse> animation during a drawer toggle so
  // the only motion the user sees over the 200ms width transition is the drawer
  // itself. Per-section header clicks still animate normally.
  const prevCollapsedRef = useRef(collapsed)
  const isDrawerToggling = prevCollapsedRef.current !== collapsed
  useEffect(() => {
    prevCollapsedRef.current = collapsed
  })
  const childCollapseTimeout: number | 'auto' = isDrawerToggling ? 0 : 'auto'

  // Auto-expand section when route changes externally
  useEffect(() => {
    const currentItem = allNavItems.find(item => location.pathname === item.path)
    if (currentItem && !expandedSections[currentItem.section]) {
      setExpandedSections(prev => ({ ...prev, [currentItem.section]: true }))
    }
  }, [location.pathname])

  const filteredItems = useMemo(() => {
    return allNavItems.filter(item => {
      if (item.staffVisible) return true
      if (item.section === 'admin' && !isAdmin) return false
      if (item.roles && !item.roles.includes(user?.role || '') && !isAdmin) return false
      return true
    })
  }, [isAdmin, user?.role])

  const itemsBySection = useMemo(() => {
    return filteredItems.reduce<Record<string, NavItem[]>>((acc, item) => {
      if (!acc[item.section]) acc[item.section] = []
      acc[item.section].push(item)
      return acc
    }, {})
  }, [filteredItems])

  // Handle real-time notifications via WebSocket
  const handleWebSocketNotification = useCallback(
    (message: { action: string; data: unknown }) => {
      const data = message.data as { title?: string; message?: string; entityType?: string; entityId?: string }
      // Show snackbar for the notification
      setNotification({
        id: generateNotificationId(),
        message: data?.title || data?.message || 'New notification',
        severity: 'info',
        autoHideDuration: 6000,
      })
    },
    [setNotification]
  )

  const handleEmailReceived = useCallback(
    (message: { action: string; data: unknown }) => {
      const data = message.data as { clientName?: string; subject?: string; fromEmail?: string }
      const sender = data?.clientName || data?.fromEmail || 'Unknown'
      setNotification({
        id: generateNotificationId(),
        message: `New email from ${sender}: ${data?.subject || '(no subject)'}`,
        severity: 'info',
        autoHideDuration: 6000,
      })
      setUnreadEmailCount((prev) => prev + 1)
    },
    [setNotification]
  )

  const handleSmsReceived = useCallback(
    (message: { action: string; data: unknown }) => {
      const data = message.data as { clientName?: string; body?: string; phoneNumber?: string }
      const sender = data?.clientName || data?.phoneNumber || 'Unknown'
      const preview = data?.body ? data.body.slice(0, 50) : '(no content)'
      setNotification({
        id: generateNotificationId(),
        message: `New SMS from ${sender}: ${preview}`,
        severity: 'info',
        autoHideDuration: 6000,
      })
      setUnreadSmsCount((prev) => prev + 1)
    },
    [setNotification]
  )

  const handleUnreadCounts = useCallback(
    (message: { action: string; data: unknown }) => {
      const data = message.data as { emails?: number; sms?: number }
      if (data?.emails !== undefined) setUnreadEmailCount(data.emails)
      if (data?.sms !== undefined) setUnreadSmsCount(data.sms)
    },
    []
  )

  const wsHandlers = useMemo(
    () => ({
      notification: handleWebSocketNotification,
      email_received: handleEmailReceived,
      sms_received: handleSmsReceived,
      unread_counts: handleUnreadCounts,
    }),
    [handleWebSocketNotification, handleEmailReceived, handleSmsReceived, handleUnreadCounts]
  )

  const { connected, send } = useWebSocket(wsHandlers)
  const sendRef = useRef(send)
  sendRef.current = send

  // Fetch unread counts from server when WebSocket connects
  useEffect(() => {
    if (connected) {
      sendRef.current('get_unread_counts')
    }
  }, [connected])

  // Mark as read and reset counts when navigating to the respective pages
  useEffect(() => {
    if (location.pathname === '/messages') {
      setUnreadSmsCount(0)
      sendRef.current('mark_read', { type: 'sms' })
    } else if (location.pathname === '/emails') {
      setUnreadEmailCount(0)
      sendRef.current('mark_read', { type: 'email' })
    }
  }, [location.pathname])

  const handleNotificationClose = () => {
    setNotification(null)
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'sales':
        return 'Sales'
      case 'client_success':
        return 'Client Success'
      case 'resume_writer':
        return 'Resume Writer'
      case 'admin':
        return 'Admin'
      default:
        return 'User'
    }
  }

  const { isModalOpen, openModal, closeModal } = useFeedback()

  const renderItemIcon = (item: NavItem) => {
    const badgeCount =
      item.path === '/messages' ? unreadSmsCount :
      item.path === '/emails' ? unreadEmailCount : 0

    return badgeCount > 0 ? (
      <Badge badgeContent={badgeCount} color="error" max={99}>
        {item.icon}
      </Badge>
    ) : (
      item.icon
    )
  }

  const drawer = (showCollapsed: boolean) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <List sx={{ px: showCollapsed ? 1 : 2, pt: 2, pb: 2, flexGrow: 1 }}>
        {standaloneNavItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={showCollapsed ? item.text : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  color: 'rgba(255,255,255,0.7)',
                  // MWA-208: anchor icon at fixed pl in collapsed mode so it doesn't slide
                  // horizontally as the drawer width transitions. pl:1.5 + List px:1 puts the
                  // icon at x=20 from drawer left, visually centered in the final 64px state.
                  pl: showCollapsed ? 1.5 : 2,
                  pr: showCollapsed ? 1.5 : 2,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                  },
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: showCollapsed ? 'auto' : 40 }}>
                  {item.icon}
                </ListItemIcon>
                {!showCollapsed && <ListItemText primary={item.text} primaryTypographyProps={{ noWrap: true }} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}

        {/* Unified structure: leaf items keep stable keys across collapsed/expanded modes
            so React reconciles in place instead of unmounting (MWA-208). */}
        {sectionOrder.map((sectionKey) => {
          const sectionItems = itemsBySection[sectionKey] || []
          if (sectionItems.length === 0) return null

          const sectionMeta = sections[sectionKey]
          const sectionLabel = !isAdmin && sectionMeta.staffName ? sectionMeta.staffName : sectionMeta.name
          const isExpanded = expandedSections[sectionKey]

          return (
            <Box key={sectionKey}>
              {/* Section header — only rendered when drawer is wide. */}
              {!showCollapsed && (
                <ListItem disablePadding sx={{ mb: 0.5, mt: 1 }}>
                  <ListItemButton
                    onClick={() => toggleSection(sectionKey)}
                    sx={{
                      borderRadius: 2,
                      color: 'rgba(255,255,255,0.7)',
                      px: 2,
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                      {sectionMeta.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={sectionLabel}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem', noWrap: true }}
                    />
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
              )}

              {/* Section children. When the drawer is collapsed, force all items visible
                  (matches the pre-MWA-208 flat-list behavior). Otherwise follow expandedSections.
                  Timeout is 0 during a drawer toggle so the children snap to the new layout
                  instead of competing with the drawer's width animation. */}
              <Collapse in={showCollapsed || isExpanded} timeout={childCollapseTimeout}>
                <List component="div" disablePadding>
                  {sectionItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                      <Tooltip
                        title={showCollapsed ? item.text : ''}
                        placement="right"
                        arrow
                        disableHoverListener={!showCollapsed}
                      >
                        <ListItemButton
                          onClick={() => navigate(item.path)}
                          selected={location.pathname === item.path}
                          sx={{
                            borderRadius: 2,
                            color: 'rgba(255,255,255,0.7)',
                            // MWA-208: anchor icon at fixed pl in collapsed mode (see Dashboard above).
                            pl: showCollapsed ? 1.5 : 4,
                            pr: showCollapsed ? 1.5 : 2,
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              color: 'white',
                              '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                            },
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                          }}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: showCollapsed ? 'auto' : 40 }}>
                            {renderItemIcon(item)}
                          </ListItemIcon>
                          {!showCollapsed && <ListItemText primary={item.text} primaryTypographyProps={{ noWrap: true }} />}
                        </ListItemButton>
                      </Tooltip>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          )
        })}
      </List>

      {/* Feedback - Anchored to Bottom. Per-page help drawers replace the global Help link (MWA-201). */}
      <Box sx={{ px: showCollapsed ? 1 : 2, pb: 2 }}>
        <Divider sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.12)' }} />
        <Tooltip title={showCollapsed ? 'Feedback' : ''} placement="right" arrow>
          <ListItemButton
            onClick={openModal}
            sx={{
              borderRadius: 2,
              color: 'rgba(255,255,255,0.7)',
              // MWA-208: anchor icon at fixed pl in collapsed mode (see Dashboard above).
              pl: showCollapsed ? 1.5 : 2,
              pr: showCollapsed ? 1.5 : 2,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: showCollapsed ? 'auto' : 40 }}>
              <FeedbackIcon />
            </ListItemIcon>
            {!showCollapsed && <ListItemText primary="Feedback" primaryTypographyProps={{ noWrap: true }} />}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden', maxWidth: '100vw' }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: 'all 0.2s ease',
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          {/* Hamburger Menu Button - Toggle side nav collapse */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="toggle menu"
            onClick={() => setCollapsed(!collapsed)}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Menu Toggle - Opens temporary drawer */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { xs: 'block', sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <BackButton home="/" />

          {/* Brand lockup — carries the wordmark itself, so no adjacent name is needed */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 2, md: 4 } }}>
            <BrandLogo height={26} sx={{ display: { xs: 'none', sm: 'block' } }} />
          </Box>

          {/* Search Bar - Centered with flexible spacing */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', px: 2 }}>
            <Box sx={{ maxWidth: 600, width: '100%' }}>
              <GlobalSearchBar />
            </Box>
          </Box>

          {/* User Menu - Far right */}
          <Box>
            <IconButton onClick={handleMenuClick} sx={{ p: 0 }}>
              <Avatar sx={{ bgcolor: colors.primary, color: 'white' }}>
                {user?.name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2">{user?.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getRoleLabel(user?.role)}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleMenuClose()
                  navigate('/profile')
                }}
              >
                <ListItemIcon>
                  <ProfileIcon fontSize="small" />
                </ListItemIcon>
                My Profile
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 }, transition: 'width 0.2s ease' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <Toolbar />
          {drawer(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: 'width 0.2s ease',
              overflowX: 'hidden',
              mt: 8,
              height: 'calc(100% - 64px)',
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer(collapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          transition: 'width 0.2s ease',
          mt: 8,
          backgroundColor: 'background.default',
          // Subtract the fixed AppBar height (mt: 8 = 64px) from the min-height
          // so AppBar + main = exactly 100vh. Using 100vh here forces body height
          // to (100vh + 64px), letting kanban pages scroll y-axis past the fixed
          // navbar even when their own height: calc(100vh - 120px) constraint fits.
          minHeight: 'calc(100vh - 64px)',
          overflowX: 'hidden',
        }}
      >
        {children}
      </Box>

      {/* Global Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={notification?.autoHideDuration ?? 4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%', color: 'white' }}
          action={notification?.action ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                notification.action?.onClick()
                handleNotificationClose()
              }}
            >
              {notification.action.label}
            </Button>
          ) : undefined}
        >
          {notification?.message}
        </Alert>
      </Snackbar>

      {/* Feedback Modal */}
      <FeedbackModal open={isModalOpen} onClose={closeModal} />
    </Box>
  )
}
